import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, ConfirmationCode, ModalBody, ModalFooter } from "@/modules/common/components";
import useResendCooldown from "@/modules/common/hooks/useResendCooldown";
import useSendRecoveryCode from "../../hooks/useSendRecoveryCode";
import useVerifyRecoveryCode from "../../hooks/useVerifyRecoveryCode";
import { VerifyRecoveryCodeFormData, verifyRecoveryCodeSchema } from "../../schemas";

const RESEND_COOLDOWN_SECONDS = 30;

interface CodeStepProps {
  email: string;
  onVerified: (code: string) => void;
  onBack: () => void;
}

const CodeStep = ({ email, onVerified, onBack }: CodeStepProps) => {
  const { cooldown: resendCooldown, start: startResendCooldown } = useResendCooldown(RESEND_COOLDOWN_SECONDS, { startActive: true });
  const verifyRecoveryCode = useVerifyRecoveryCode();
  const sendRecoveryCode = useSendRecoveryCode();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyRecoveryCodeFormData>({ resolver: zodResolver(verifyRecoveryCodeSchema), defaultValues: { code: "" } });

  const onSubmit = async (values: VerifyRecoveryCodeFormData) => {
    await verifyRecoveryCode.mutateAsync({ email, code: Number(values.code) });
    onVerified(values.code);
  };

  const onResend = async () => {
    if (resendCooldown > 0 || sendRecoveryCode.isPending) return;

    await sendRecoveryCode.mutateAsync({ email });
    startResendCooldown();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalBody className="gap-1">
        <label className="text-sm">
          Código de verificação
          <span className="text-red-500"> *</span>
        </label>
        <Controller
          name="code"
          control={control}
          render={({ field }) => <ConfirmationCode value={field.value} onChange={field.onChange} />}
        />
        {errors.code && <p className="mt-1 text-xs text-red-500">{errors.code.message}</p>}

        <Button
          type="button"
          variant="text"
          onClick={onResend}
          className="mt-3 self-start text-sm font-semibold text-ui-primary disabled:cursor-not-allowed disabled:text-ui-text-muted"
          label={resendCooldown > 0 ? `Reenviar código em ${resendCooldown}s` : "Reenviar código"}
          disabled={resendCooldown > 0 || sendRecoveryCode.isPending}
          data-test="forgot-password-resend-code"
        />
      </ModalBody>
      <ModalFooter className="justify-between">
        <Button type="button" variant="outlined" label="Voltar" className="w-full" onClick={onBack} />
        <Button
          type="submit"
          className="w-full"
          label="Verificar código"
          disabled={verifyRecoveryCode.isPending}
          loading={verifyRecoveryCode.isPending}
          data-test="forgot-password-verify-code"
        />
      </ModalFooter>
    </form>
  );
};

export default CodeStep;
